import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {twoFactorApi, getIsTwoFactorChallenge} from '@api/twoFactor/api';
import {getErrorMessage} from '@utils/requests/error';
import {parseJWT, getIsDelegationToken} from '@utils/jwtUtils';
import {TWO_FACTOR_STATUS} from '@constants/twoFactor';
import {DISMISSED_2FA_STORAGE_KEY} from '@hooks/useGet2FAEnforcementStatus';
import type {AppDispatch, RootState} from '@src/client/index';
import type {TwoFactorStatus, LoginResponse, OAuthTokenResource, TwoFactorChallengeResource} from '@api/twoFactor/api.d';

const TWO_FACTOR_ISSUER = 'ValidSign';

interface ErrorMessage {
    messageKey: string;
    parameters?: Record<string, unknown>;
}

interface TwoFactorState {
    is2FAEnabled: boolean;
    isTwoFactorStatusFetching: boolean;
    wasTwoFactorStatusFetched: boolean;
    twoFactorStatusFetchError: ErrorMessage | null;
    twoFactorSecret: string | null;
    isTwoFactorSetupFetching: boolean;
    twoFactorSetupFetchError: ErrorMessage | null;
    isVerifyingSetup: boolean;
    verifySetupError: ErrorMessage | null;
    backupCodes: string[];
    isDisabling: boolean;
    disableError: ErrorMessage | null;
    isLoggingIn: boolean;
    loginError: ErrorMessage | null;
    challengeToken: string | null;
    accessToken: string | null;
    hasDismissed2FA: boolean;
    wasDismissedThisSession: boolean;
    isDismissing2FA: boolean;
    disable2FAForUserError: {messageKey: string} | null;
}

const initialState: TwoFactorState = {
    is2FAEnabled: false,
    isTwoFactorStatusFetching: false,
    wasTwoFactorStatusFetched: false,
    twoFactorStatusFetchError: null,
    twoFactorSecret: null,
    isTwoFactorSetupFetching: false,
    twoFactorSetupFetchError: null,
    isVerifyingSetup: false,
    verifySetupError: null,
    backupCodes: [],
    isDisabling: false,
    disableError: null,
    isLoggingIn: false,
    loginError: null,
    challengeToken: null,
    accessToken: null,
    hasDismissed2FA: false,
    wasDismissedThisSession: false,
    isDismissing2FA: false,
    disable2FAForUserError: null
};

export const twoFactorSlice = createSlice({
    name: 'twoFactor',
    initialState,
    reducers: {
        setIs2FAEnabled: (state, action: PayloadAction<boolean>) => {
            state.is2FAEnabled = action.payload;
        },
        setIsTwoFactorStatusFetching: (state, action: PayloadAction<boolean>) => {
            state.isTwoFactorStatusFetching = action.payload;
        },
        setWasTwoFactorStatusFetched: (state, action: PayloadAction<boolean>) => {
            state.wasTwoFactorStatusFetched = action.payload;
        },
        setTwoFactorStatusFetchError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.twoFactorStatusFetchError = action.payload;
        },
        setTwoFactorSecret: (state, action: PayloadAction<string | null>) => {
            state.twoFactorSecret = action.payload;
        },
        setIsTwoFactorSetupFetching: (state, action: PayloadAction<boolean>) => {
            state.isTwoFactorSetupFetching = action.payload;
        },
        setTwoFactorSetupFetchError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.twoFactorSetupFetchError = action.payload;
        },
        setIsVerifyingSetup: (state, action: PayloadAction<boolean>) => {
            state.isVerifyingSetup = action.payload;
        },
        setVerifySetupError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.verifySetupError = action.payload;
        },
        setBackupCodes: (state, action: PayloadAction<string[]>) => {
            state.backupCodes = action.payload;
        },
        resetTwoFactorSetup: (state) => {
            state.twoFactorSecret = null;
            state.twoFactorSetupFetchError = null;
            state.verifySetupError = null;
            state.backupCodes = [];
        },
        clearBackupCodes: (state) => {
            state.backupCodes = [];
        },
        setIsDisabling: (state, action: PayloadAction<boolean>) => {
            state.isDisabling = action.payload;
        },
        setDisableError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.disableError = action.payload;
        },
        setIsLoggingIn: (state, action: PayloadAction<boolean>) => {
            state.isLoggingIn = action.payload;
        },
        setLoginError: (state, action: PayloadAction<ErrorMessage | null>) => {
            state.loginError = action.payload;
        },
        setChallengeToken: (state, action: PayloadAction<string | null>) => {
            state.challengeToken = action.payload;
        },
        setAccessToken: (state, action: PayloadAction<string | null>) => {
            state.accessToken = action.payload;
        },
        setHasDismissed2FA: (state, action: PayloadAction<boolean>) => {
            state.hasDismissed2FA = action.payload;
        },
        setWasDismissedThisSession: (state, action: PayloadAction<boolean>) => {
            state.wasDismissedThisSession = action.payload;
        },
        setIsDismissing2FA: (state, action: PayloadAction<boolean>) => {
            state.isDismissing2FA = action.payload;
        },
        setDisable2FAForUserError: (state, action: PayloadAction<{messageKey: string} | null>) => {
            state.disable2FAForUserError = action.payload;
        }
    }
});

export const {
    setIs2FAEnabled,
    setIsTwoFactorStatusFetching,
    setWasTwoFactorStatusFetched,
    setTwoFactorStatusFetchError,
    setTwoFactorSecret,
    setIsTwoFactorSetupFetching,
    setTwoFactorSetupFetchError,
    setIsVerifyingSetup,
    setVerifySetupError,
    setBackupCodes,
    resetTwoFactorSetup,
    clearBackupCodes,
    setIsDisabling,
    setDisableError,
    setIsLoggingIn,
    setLoginError,
    setChallengeToken,
    setAccessToken,
    setHasDismissed2FA,
    setWasDismissedThisSession,
    setIsDismissing2FA,
    setDisable2FAForUserError
} = twoFactorSlice.actions;

export const fetchTwoFactorStatus = ({isForceRefresh = false} = {}) => async (dispatch: AppDispatch, getState: () => RootState): Promise<TwoFactorStatus | false> => {
    const state = getState();
    const accessToken = state.user?.accessToken;
    if (getIsDelegationToken(accessToken)) {
        return false;
    }

    const {isTwoFactorStatusFetching, wasTwoFactorStatusFetched, is2FAEnabled} = state.twoFactor;
    if (!isForceRefresh && (isTwoFactorStatusFetching || wasTwoFactorStatusFetched)) {
        return is2FAEnabled ? TWO_FACTOR_STATUS.ENABLED : TWO_FACTOR_STATUS.DISABLED;
    }

    dispatch(setIsTwoFactorStatusFetching(true));
    dispatch(setTwoFactorStatusFetchError(null));

    try {
        const result = await twoFactorApi.getTwoFactorStatus({});
        const status = result.data.status;
        dispatch(setIs2FAEnabled(status === TWO_FACTOR_STATUS.ENABLED));
        dispatch(setHasDismissed2FA(result.data.has_dismissed ?? false));
        dispatch(setIsTwoFactorStatusFetching(false));
        dispatch(setWasTwoFactorStatusFetched(true));
        return status;
    } catch (err: unknown) {
        dispatch(setIsTwoFactorStatusFetching(false));
        dispatch(setWasTwoFactorStatusFetched(true));
        dispatch(setTwoFactorStatusFetchError(getErrorMessage(err)));
        return false;
    }
};

export const fetchTwoFactorSetup = () => async (dispatch: AppDispatch): Promise<string | false> => {
    dispatch(setIsTwoFactorSetupFetching(true));
    dispatch(setTwoFactorSetupFetchError(null));

    try {
        const result = await twoFactorApi.getTwoFactorSetup({});
        const {secret} = result.data;
        dispatch(setTwoFactorSecret(secret));
        dispatch(setIsTwoFactorSetupFetching(false));
        return secret;
    } catch (err: unknown) {
        dispatch(setIsTwoFactorSetupFetching(false));
        dispatch(setTwoFactorSetupFetchError(getErrorMessage(err)));
        return false;
    }
};

export const verifyTwoFactorSetup = (code: string) => async (dispatch: AppDispatch): Promise<string[] | false> => {
    dispatch(setIsVerifyingSetup(true));
    dispatch(setVerifySetupError(null));

    try {
        const result = await twoFactorApi.verifyTwoFactorSetup({data: {code}});
        const backupCodes = result.data.backup_codes;
        dispatch(setBackupCodes(backupCodes));
        dispatch(setIsVerifyingSetup(false));
        await (dispatch as any)(fetchTwoFactorStatus({isForceRefresh: true}));
        return backupCodes;
    } catch (err: unknown) {
        dispatch(setIsVerifyingSetup(false));
        dispatch(setVerifySetupError(getErrorMessage(err)));
        return false;
    }
};

export const disableTwoFactor = (code: string) => async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(setIsDisabling(true));
    dispatch(setDisableError(null));

    try {
        await twoFactorApi.disableTwoFactor({data: {code}});
        dispatch(setIsDisabling(false));
        await (dispatch as any)(fetchTwoFactorStatus({isForceRefresh: true}));
        return true;
    } catch (err: unknown) {
        dispatch(setIsDisabling(false));
        dispatch(setDisableError(getErrorMessage(err)));
        return false;
    }
};

export const dismissTwoFactorSetup = () => async (dispatch: AppDispatch, getState: () => RootState): Promise<boolean> => {
    dispatch(setIsDismissing2FA(true));
    try {
        await twoFactorApi.dismissTwoFactorSetup({});
        const accessToken = getState().user?.accessToken;
        const sessionId = accessToken ? parseJWT(accessToken)?.sid : null;
        if (sessionId) {
            sessionStorage.setItem(DISMISSED_2FA_STORAGE_KEY, String(sessionId));
        }
        dispatch(setHasDismissed2FA(true));
        dispatch(setWasDismissedThisSession(true));
        dispatch(setIsDismissing2FA(false));
        await (dispatch as any)(fetchTwoFactorStatus({isForceRefresh: true}));
        return true;
    } catch (err: unknown) {
        dispatch(setIsDismissing2FA(false));
        return false;
    }
};

export const disableTwoFactorForUserAction = (userId: string, activeAccountId: string) => async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(setDisable2FAForUserError(null));
    try {
        await twoFactorApi.disableTwoFactorForUser({
            userId,
            options: {headers: {'Session-Account-Id': activeAccountId}}
        });
        return true;
    } catch (err: unknown) {
        dispatch(setDisable2FAForUserError(getErrorMessage(err)));
        return false;
    }
};

export function getTotpUri(email: string, secret: string): string {
    const encodedEmail = encodeURIComponent(email);
    const encodedIssuer = encodeURIComponent(TWO_FACTOR_ISSUER);
    return `otpauth://totp/${encodedIssuer}:${encodedEmail}?secret=${secret}&issuer=${encodedIssuer}`;
}

interface LoginResult {
    type: 'token' | 'challenge';
    data: OAuthTokenResource | TwoFactorChallengeResource;
}

export const login = (email: string, password: string) => async (dispatch: AppDispatch): Promise<LoginResult | false> => {
    dispatch(setIsLoggingIn(true));
    dispatch(setLoginError(null));
    dispatch(setChallengeToken(null));
    dispatch(setAccessToken(null));

    try {
        const result = await twoFactorApi.login({
            data: {email, password},
            options: {useS2SAuth: true}
        });

        dispatch(setIsLoggingIn(false));

        const loginResponse = result.data;

        if (getIsTwoFactorChallenge(loginResponse)) {
            const challengeData = loginResponse.data as TwoFactorChallengeResource;
            dispatch(setChallengeToken(challengeData.challenge_token));
            return {type: 'challenge', data: challengeData};
        }

        const tokenData = loginResponse.data as OAuthTokenResource;
        dispatch(setAccessToken(tokenData.access_token));
        return {type: 'token', data: tokenData};
    } catch (err: unknown) {
        dispatch(setIsLoggingIn(false));
        dispatch(setLoginError(getErrorMessage(err)));
        return false;
    }
};

export const verifyLoginChallenge = (challengeToken: string, code: string) => async (dispatch: AppDispatch): Promise<OAuthTokenResource | false> => {
    dispatch(setIsLoggingIn(true));
    dispatch(setLoginError(null));

    try {
        const result = await twoFactorApi.verifyTwoFactor({
            data: {challenge_token: challengeToken, code},
            options: {useS2SAuth: true}
        });

        dispatch(setIsLoggingIn(false));
        dispatch(setChallengeToken(null));

        const tokenData = result.data;
        dispatch(setAccessToken(tokenData.access_token));
        return tokenData;
    } catch (err: unknown) {
        dispatch(setIsLoggingIn(false));
        dispatch(setLoginError(getErrorMessage(err)));
        return false;
    }
};

export const selectIs2FAEnabled = (state: RootState) => state.twoFactor.is2FAEnabled;
export const selectIsTwoFactorStatusFetching = (state: RootState) => state.twoFactor.isTwoFactorStatusFetching;
export const selectWasTwoFactorStatusFetched = (state: RootState) => state.twoFactor.wasTwoFactorStatusFetched;
export const selectTwoFactorStatusFetchError = (state: RootState) => state.twoFactor.twoFactorStatusFetchError;
export const selectTwoFactorSecret = (state: RootState) => state.twoFactor.twoFactorSecret;
export const selectIsTwoFactorSetupFetching = (state: RootState) => state.twoFactor.isTwoFactorSetupFetching;
export const selectTwoFactorSetupFetchError = (state: RootState) => state.twoFactor.twoFactorSetupFetchError;
export const selectIsVerifyingSetup = (state: RootState) => state.twoFactor.isVerifyingSetup;
export const selectVerifySetupError = (state: RootState) => state.twoFactor.verifySetupError;
export const selectBackupCodes = (state: RootState) => state.twoFactor.backupCodes;
export const selectIsDisabling = (state: RootState) => state.twoFactor.isDisabling;
export const selectDisableError = (state: RootState) => state.twoFactor.disableError;
export const selectIsLoggingIn = (state: RootState) => state.twoFactor.isLoggingIn;
export const selectLoginError = (state: RootState) => state.twoFactor.loginError;
export const selectChallengeToken = (state: RootState) => state.twoFactor.challengeToken;
export const selectAccessToken = (state: RootState) => state.twoFactor.accessToken;
export const selectHasDismissed2FA = (state: RootState) => state.twoFactor.hasDismissed2FA;
export const selectWasDismissedThisSession = (state: RootState) => state.twoFactor.wasDismissedThisSession;
export const selectIsDismissing2FA = (state: RootState) => state.twoFactor.isDismissing2FA;
export const selectDisable2FAForUserError = (state: RootState) => state.twoFactor.disable2FAForUserError;

export default twoFactorSlice.reducer;
